// ─────────────────────────────────────────────────────────────────────────────
// Collection — persists earned cards, fragments and lootbox count
// ─────────────────────────────────────────────────────────────────────────────

const STORAGE_KEY = "pazurumon_collection_v1";

const FRAGMENTS_PER_LOOTBOX = 100;

function defaultCollection() {
  return {
    // Array of card IDs earned (duplicates allowed)
    cards: [],
    // Fragments per element
    fragments: { fire: 0, water: 0, earth: 0, electric: 0, aether: 0, corruption: 0 },
    // Ready-to-open lootboxes
    lootboxes: 0,
    // Total playtime in ms (incremented externally)
    playtimeMs: 0,
  };
}

export const Collection = {
  load() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return defaultCollection();
      const saved = JSON.parse(raw);
      const base = defaultCollection();
      return {
        cards:       Array.isArray(saved.cards) ? saved.cards : [],
        fragments:   { ...base.fragments, ...(saved.fragments || {}) },
        lootboxes:   Number(saved.lootboxes) || 0,
        playtimeMs:  Number(saved.playtimeMs) || 0,
      };
    } catch {
      return defaultCollection();
    }
  },

  save(col) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(col));
  },

  // ── Lootboxes ──────────────────────────────────────────────────────────────

  addLootbox(count = 1) {
    const col = this.load();
    col.lootboxes = (col.lootboxes || 0) + count;
    this.save(col);
    return col.lootboxes;
  },

  getLootboxCount() {
    return this.load().lootboxes || 0;
  },

  consumeLootbox() {
    const col = this.load();
    if (col.lootboxes <= 0) return false;
    col.lootboxes -= 1;
    this.save(col);
    return true;
  },

  // ── Cards ──────────────────────────────────────────────────────────────────

  addCards(cardIds) {
    const col = this.load();
    col.cards.push(...cardIds);
    this.save(col);
  },

  getCardIds() {
    return this.load().cards;
  },

  /** How many copies of a specific card does the player own? */
  countCard(cardId) {
    return this.load().cards.filter(id => id === cardId).length;
  },

  /** Unique card IDs owned (no duplicates) */
  getUniqueCardIds() {
    return [...new Set(this.load().cards)];
  },

  // ── Fragments ──────────────────────────────────────────────────────────────

  /**
   * Award fragments earned from combos.
   * comboSize 4  → 5  fragments of that element
   * comboSize 5  → 10 rare fragments (bonus to all elements)
   * comboSize 6+ → 20 fragments of that element + all elements +5
   */
  awardFragments(element, comboSize) {
    const col = this.load();
    if (!col.fragments[element]) col.fragments[element] = 0;

    if (comboSize >= 6) {
      col.fragments[element] = (col.fragments[element] || 0) + 20;
      for (const el of Object.keys(col.fragments)) {
        col.fragments[el] = (col.fragments[el] || 0) + 5;
      }
    } else if (comboSize === 5) {
      for (const el of Object.keys(col.fragments)) {
        col.fragments[el] = (col.fragments[el] || 0) + 10;
      }
    } else if (comboSize === 4) {
      col.fragments[element] = (col.fragments[element] || 0) + 5;
    }

    // Convert accumulated fragments → lootboxes
    let totalFragments = Object.values(col.fragments).reduce((s, v) => s + v, 0);
    while (totalFragments >= FRAGMENTS_PER_LOOTBOX) {
      col.lootboxes += 1;
      totalFragments -= FRAGMENTS_PER_LOOTBOX;
      // Deduct evenly across elements
      let toDeduct = FRAGMENTS_PER_LOOTBOX;
      for (const el of Object.keys(col.fragments)) {
        const take = Math.min(col.fragments[el], toDeduct);
        col.fragments[el] -= take;
        toDeduct -= take;
        if (toDeduct <= 0) break;
      }
    }

    this.save(col);
    return col;
  },

  /**
   * Add a raw fragment amount of a given element (used for duplicate card rewards).
   * Converts to lootboxes automatically when threshold is reached.
   */
  addRawFragments(element, amount) {
    const col = this.load();
    const el = col.fragments[element] !== undefined ? element : "aether";
    col.fragments[el] = (col.fragments[el] || 0) + amount;

    // Convert accumulated fragments → lootboxes
    let total = Object.values(col.fragments).reduce((s, v) => s + v, 0);
    while (total >= FRAGMENTS_PER_LOOTBOX) {
      col.lootboxes += 1;
      total -= FRAGMENTS_PER_LOOTBOX;
      let toDeduct = FRAGMENTS_PER_LOOTBOX;
      for (const k of Object.keys(col.fragments)) {
        const take = Math.min(col.fragments[k], toDeduct);
        col.fragments[k] -= take;
        toDeduct -= take;
        if (toDeduct <= 0) break;
      }
    }

    this.save(col);
    return col;
  },

  getTotalFragments() {
    const frags = this.load().fragments;
    return Object.values(frags).reduce((s, v) => s + v, 0);
  },

  getFragmentProgress() {
    const total = this.getTotalFragments();
    return {
      current: total % FRAGMENTS_PER_LOOTBOX,
      needed: FRAGMENTS_PER_LOOTBOX,
      percent: Math.floor((total % FRAGMENTS_PER_LOOTBOX) / FRAGMENTS_PER_LOOTBOX * 100),
    };
  },

  // ── Playtime ───────────────────────────────────────────────────────────────

  addPlaytime(ms) {
    const col = this.load();
    col.playtimeMs = (col.playtimeMs || 0) + ms;
    this.save(col);
    return col.playtimeMs;
  },

  getPlaytimeHours() {
    return Math.floor((this.load().playtimeMs || 0) / 3_600_000);
  },

  // ── Debug ──────────────────────────────────────────────────────────────────

  reset() {
    this.save(defaultCollection());
  },
};
